/* global google */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import classnames from 'classnames';
import scrollIntoView from 'scroll-into-view';
import { parseAddress } from '../../models/address';
import { isMobileDevice } from 'shared/utils';
import { PrimaryButton, SecondaryButton } from 'scy/buttons';
import { zIndex1 } from 'scy/utils.mod.scss';
import Marker from 'images/icons/controls/map-marker.svg?react-svg';
import Arrow from 'images/icons/controls/arrow-continue.svg?react-svg';
import LoadingSpinner from 'scy/LoadingSpinner';
import styles from './styles.mod.scss';

function useGoogleAutocomplete({ inputRef, handlePlaceChange, handleSubmit }) {
  const autocomplete = useRef();
  const [listening, setListening] = useState(false);

  useEffect(() => {
    const input = inputRef.current;
    if (!input || !google) return;

    if (!listening) {
      autocomplete.current = new google.maps.places.Autocomplete(
        inputRef.current,
        { types: ['geocode'] }
      );
    }

    // Ensures hitting Enter only submits the form when a place hasn't been selected
    const placeSelectedHandler = e => {
      if (e.which === 13) {
        if (document.getElementsByClassName('pac-item-selected').length) {
          e.preventDefault();
          return false;
        } else {
          return handleSubmit(e);
        }
      }
    };
    if (!listening) {
      autocomplete.current.addListener('place_changed', () => {
        const place = autocomplete.current.getPlace();
        if (place) handlePlaceChange(place);
      });
      input.addEventListener('keydown', placeSelectedHandler);
      setListening(true);
    }
  }, [inputRef, handlePlaceChange, handleSubmit, listening]);

  function updateAutocompleteBounds({ latitude, longitude, accuracy }) {
    if (!autocomplete.current || !google) return;
    const circle = new google.maps.Circle({
      center: { lat: latitude, lng: longitude },
      radius: accuracy
    });
    autocomplete.current.setBounds(circle.getBounds());
  }

  return { updateAutocompleteBounds };
}

function useGoogleGeocoder() {
  const geocoder = useRef();
  useEffect(() => {
    if (!google) return;
    geocoder.current = new google.maps.Geocoder();
  }, []);
  function geocode(place, callback) {
    return geocoder.current ? geocoder.current.geocode(place, callback) : null;
  }
  return { geocode };
}

function useBrowserGeolocation() {
  const [canGeolocate, setCanGeolocate] = useState(false);
  useEffect(() => {
    setCanGeolocate(!!navigator.geolocation);
  }, []);
  function requestGeolocation(onSuccess, onError) {
    return (
      canGeolocate &&
      navigator.geolocation.getCurrentPosition(onSuccess, onError)
    );
  }
  return { canGeolocate, requestGeolocation };
}

export default function Address({
  onSuccess,
  autoFocus,
  scrollOnMobileFocus,
  className,
  id = 'address-input',
  defaultValue = ''
}) {
  const inputRef = useRef();
  const [displayAddress, setDisplayAddress] = useState(defaultValue);

  const { geocode } = useGoogleGeocoder();

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      const address = inputRef.current.value;
      if (!address || !geocode) return;
      return geocode({ address }, res => {
        if (!res || !res.length) return;
        const parsed = parseAddress(res[0]);
        return onSuccess(parsed);
      });
    },
    [geocode, onSuccess]
  );

  const handlePlaceChange = useCallback(
    place => {
      const parsed = parseAddress(place);
      if (parsed && parsed.address) {
        setDisplayAddress(parsed.address);
      }
      return onSuccess(parsed);
    },
    [onSuccess]
  );

  const { updateAutocompleteBounds } = useGoogleAutocomplete({
    inputRef,
    handlePlaceChange,
    handleSubmit
  });

  const { canGeolocate, requestGeolocation } = useBrowserGeolocation();

  const [geolocationLoading, setGeolocationLoading] = useState(false);

  function geolocate() {
    setGeolocationLoading(true);
    const handleSuccess = ({ coords }) => {
      updateAutocompleteBounds(coords);
      const location = { lat: coords.latitude, lng: coords.longitude };
      return geocode({ location }, (result, status) => {
        if (status === 'OK') {
          handlePlaceChange(result[0]);
        }
        setGeolocationLoading(false);
      });
    };
    const handleError = () => setGeolocationLoading(false);
    return requestGeolocation(handleSuccess, handleError);
  }

  useEffect(() => {
    const isDesktop = !isMobileDevice();
    if (autoFocus && isDesktop && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  function handleFocus() {
    if (!scrollOnMobileFocus) return;
    const isSmallMobileDevice = isMobileDevice() && window.innerWidth < 750;
    if (isSmallMobileDevice) {
      scrollIntoView(inputRef.current, { align: { top: 0, topOffset: 55 } });
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={classnames(styles.form, className)}
    >
      {geolocationLoading && <LoadingSpinner black className={zIndex1} />}
      <SecondaryButton
        noBorder
        className={classnames(styles.marker, {
          [styles.geolocationLoading]: geolocationLoading
        })}
        title="Use Current Location"
        onClick={geolocate}
        disabled={!canGeolocate || geolocationLoading}
        type="button"
      >
        <Marker />
      </SecondaryButton>
      <input
        type="text"
        className={classnames(styles.input, {
          [styles.geolocationLoading]: geolocationLoading
        })}
        id={id}
        placeholder="Enter Delivery Address"
        ref={inputRef}
        onFocus={handleFocus}
        value={displayAddress}
        onChange={e => setDisplayAddress(e.target.value)}
      />
      <PrimaryButton
        type="submit"
        className={styles.button}
        disabled={geolocationLoading}
      >
        <Arrow />
      </PrimaryButton>
    </form>
  );
}
